<template>
  <section class="monitor-section online-users" aria-labelledby="online-heading">
    <header class="section-heading">
      <div>
        <h2 id="online-heading">在线用户与当前使用</h2>
        <p>
          按已认证连接去重；自动刷新开启时每 5
          秒更新。前台状态表示页面可见，任务数来自实际运行记录。
        </p>
      </div>
      <span>采样 {{ formatTime(store.onlineUsers?.generatedAt) }}</span>
    </header>
    <div class="filter-toolbar">
      <a-input-search
        v-model:value="searchDraft"
        placeholder="用户昵称 / ID，回车查询"
        aria-label="查询在线用户"
        class="id-search"
        allow-clear
        @search="store.onlineQuery.search = searchDraft.trim()"
      />
      <a-select
        v-model:value="store.onlineQuery.client"
        aria-label="在线终端筛选"
        :options="clientOptions"
      />
      <a-select
        v-model:value="store.onlineQuery.state"
        aria-label="用户活动状态筛选"
        :options="stateOptions"
      />
      <span v-if="store.onlineCurrent"
        >{{ store.onlineUsers?.warnings.length ? '已采样匹配' : '共' }}
        {{ store.onlineUsers?.total ?? 0 }} 人</span
      >
    </div>
    <a-alert v-if="store.onlineError" type="error" show-icon :message="store.onlineError"
      ><template #action
        ><a-button size="small" @click="store.loadOnlineUsers">重试</a-button></template
      ></a-alert
    >
    <a-alert
      v-if="store.onlineCurrent && store.onlineUsers?.warnings.length"
      type="warning"
      show-icon
      :message="store.onlineUsers.warnings.join('；')"
    />
    <a-table
      :columns="columns"
      :data-source="rows"
      row-key="id"
      size="middle"
      :pagination="pagination"
      :loading="store.onlineLoading && !store.onlineCurrent"
      :show-expand-column="false"
      :expanded-row-keys="expanded"
      @change="store.onlineQuery.page = $event.current ?? 1"
    >
      <template #emptyText
        ><a-empty
          :description="
            store.onlineError || store.onlineUsers?.warnings.length
              ? '在线状态暂不可完整读取，请重试'
              : '当前没有符合条件的在线用户'
          "
      /></template>
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'user'">
          <button
            class="text-action service-name"
            :aria-expanded="expanded.includes(record.id)"
            @click="toggle(record.id)"
          >
            {{ record.displayName }}
          </button>
          <small class="user-id">{{ record.id }}</small>
          <small class="mobile-connections">{{ connections(record) }}</small>
        </template>
        <template v-else-if="column.key === 'client'"
          >{{ connections(record)
          }}<small v-if="record.devices">运行设备 {{ record.devices }} 台</small></template
        >
        <template v-else-if="column.key === 'usage'">
          <span>{{ stateLabels[record.state as MonitorOnlineUser['state']] }}</span>
          <small>{{ featureNames(record) || '尚无有效活动上报' }}</small>
          <small>{{ taskSummary(record) }}</small>
        </template>
        <template v-else-if="column.key === 'workspace'"
          >{{ workspaceNames(record) || '未加入工作台'
          }}<small>查看会话 {{ record.sessionIds.length }} 个</small></template
        >
        <template v-else-if="column.key === 'seen'"
          >{{ formatTime(record.lastSeenAt)
          }}<small>本次连接 {{ duration(record.connectedAt) }}</small></template
        >
      </template>
      <template #expandedRowRender="{ record }">
        <dl class="user-facts">
          <div>
            <dt>本次最早连接</dt>
            <dd>{{ formatTime(record.connectedAt) }}</dd>
          </div>
          <div>
            <dt>最近活动 / 心跳</dt>
            <dd>{{ formatTime(record.lastSeenAt) }}</dd>
          </div>
          <div>
            <dt>工作台</dt>
            <dd>
              <span v-for="workspace in record.workspaces" :key="workspace.id"
                >{{ workspace.name }} <code>{{ workspace.id }}</code
                ><br /></span
              ><span v-if="!record.workspaces.length">未加入工作台</span>
            </dd>
          </div>
          <div>
            <dt>正在查看的会话</dt>
            <dd>
              <code v-for="id in record.sessionIds" :key="id">{{ id }}<br /></code
              ><span v-if="!record.sessionIds.length">未订阅会话</span>
            </dd>
          </div>
          <div>
            <dt>Agent 任务</dt>
            <dd>{{ tasks(record.agentTasks) }}</dd>
          </div>
          <div>
            <dt>创意任务</dt>
            <dd>{{ tasks(record.creativeTasks) }}</dd>
          </div>
        </dl>
        <p class="activity-note">
          功能和可见状态来自客户端上报；工作台与会话表示授权订阅，后台任务可能继续运行。
        </p>
      </template>
    </a-table>
  </section>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import type { MonitorOnlineUser, MonitorUserFeature } from 'shared';
import { useAdminRuntimeMonitorStore } from '@/stores/admin-runtime-monitor.store';
import { formatTime, taskStatusLabels } from './monitor-format';
const store = useAdminRuntimeMonitorStore();
const searchDraft = ref(store.onlineQuery.search);
const expanded = ref<string[]>([]);
const rows = computed(() => (store.onlineCurrent ? (store.onlineUsers?.items ?? []) : []));
const pagination = computed(() => ({
  current: store.onlineQuery.page,
  pageSize: 20,
  total: store.onlineCurrent ? (store.onlineUsers?.total ?? 0) : 0,
  showSizeChanger: false,
  hideOnSinglePage: true,
}));
const stateLabels = { foreground: '页面在前台', background: '页面在后台', unknown: '活动未验证' };
const featureLabels: Record<MonitorUserFeature, string> = {
  workspace: '工作台',
  chat: 'Agent 会话',
  component: '组件开发 / 使用',
  tasks: '任务管理',
  library: '资产库',
  files: '文件',
  knowledge: '知识库',
  creative: '创意生成',
  settings: '设置',
  marketplace: '市场',
  other: '其他功能',
};
const clientOptions = [
  { value: 'all', label: '全部终端' },
  { value: 'web', label: '网页' },
  { value: 'desktop', label: '桌面' },
  { value: 'mobile', label: '移动端' },
];
const stateOptions = [
  { value: 'all', label: '全部活动状态' },
  ...Object.entries(stateLabels).map(([value, label]) => ({ value, label })),
];
const columns = [
  { title: '用户 / 标识', key: 'user' },
  { title: '终端连接', key: 'client', width: 170, responsive: ['md'] },
  { title: '当前使用', key: 'usage' },
  { title: '工作台 / 会话', key: 'workspace', width: 230, responsive: ['md'] },
  { title: '最近心跳 / 连接时长', key: 'seen', width: 190, responsive: ['md'] },
];
function featureNames(user: MonitorOnlineUser): string {
  return user.features.map((feature) => featureLabels[feature]).join('、');
}
function workspaceNames(user: MonitorOnlineUser): string {
  return user.workspaces.map((workspace) => workspace.name).join('、');
}
function toggle(id: string): void {
  expanded.value = expanded.value.includes(id)
    ? expanded.value.filter((value) => value !== id)
    : [...expanded.value, id];
}
function connections(user: MonitorOnlineUser): string {
  const labels = { web: '网页', desktop: '桌面页面', mobile: '移动端', runtime: '桌面运行' };
  return Object.entries(user.connections)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => `${labels[key as keyof typeof labels]} ${count}`)
    .join(' · ');
}
function tasks(counts: Record<string, number> | null): string {
  return counts === null
    ? '暂未取得数值'
    : Object.entries(counts)
        .map(([status, count]) => `${taskStatusLabels[status] ?? status} ${count}`)
        .join(' · ') || '无活跃任务';
}
function taskSummary(user: MonitorOnlineUser): string {
  const sum = (counts: Record<string, number> | null) =>
    counts === null ? '—' : Object.values(counts).reduce((total, count) => total + count, 0);
  return `Agent ${sum(user.agentTasks)} · 创意 ${sum(user.creativeTasks)}`;
}
function duration(at: string | null): string {
  if (!at || !store.onlineUsers) return '—';
  const minutes = Math.max(
    0,
    Math.floor((Date.parse(store.onlineUsers.generatedAt) - Date.parse(at)) / 60000),
  );
  return minutes < 1
    ? '不足 1 分钟'
    : minutes < 60
      ? `${minutes} 分钟`
      : `${Math.floor(minutes / 60)} 小时 ${minutes % 60} 分钟`;
}
</script>
<style scoped>
.online-users :deep(.ant-empty-description) {
  color: var(--aw-text-secondary);
}
.user-id,
.user-facts code {
  overflow-wrap: anywhere;
  font-size: 12px;
}
.user-facts {
  margin: 0;
}
.user-facts > div {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 12px;
  margin: 8px 0;
}
.user-facts dt,
.activity-note {
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.user-facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.activity-note {
  line-height: 1.7;
}
.online-users .mobile-connections {
  display: none;
}
@media (max-width: 767px) {
  .online-users .mobile-connections {
    display: block;
  }
  .user-facts > div {
    grid-template-columns: 95px minmax(0, 1fr);
    gap: 8px;
  }
}
</style>
